import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

// Editorial project block: poster left, details right, "More Info" link.
// Mirrors the original landing-page Upcoming Project layout exactly.
export default function ProjectCard({ project, testIdPrefix = "project" }) {
  const p = project;
  const title = `${p.titleLine1} ${p.titleLine2}`;

  return (
    <div className="grid grid-cols-12 gap-6 md:gap-10 items-end" data-testid={`${testIdPrefix}-card-${p.slug}`}>
      {/* Image */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="col-span-12 md:col-span-6 overflow-hidden"
      >
        <Link to={p.path} className="block">
          <img
            src={p.image}
            alt={title}
            className="w-full h-auto block max-w-[480px] md:max-w-none mx-auto"
            data-testid={`${testIdPrefix}-image`}
          />
        </Link>
      </motion.div>

      {/* Details */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="col-span-12 md:col-span-6 md:pl-12 lg:pl-20"
      >
        <span className="label">{p.partner}</span>
        <h2
          data-testid={`${testIdPrefix}-title`}
          className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-black mt-5"
        >
          {p.titleLine1}
          <br />
          <span className="italic font-light">{p.titleLine2}</span>
        </h2>

        <p className="font-body font-light italic text-base md:text-lg text-black/70 mt-6">
          {p.artists}
        </p>
        {p.collection && (
          <p className="font-body font-light text-sm text-black/50 mt-2" data-testid={`${testIdPrefix}-collection`}>
            {p.collection}
          </p>
        )}

        <div className="mt-10 mb-12 space-y-3">
          <div className="flex gap-6">
            <span className="label w-20 shrink-0">Date</span>
            <span className="font-body font-light text-base md:text-lg">{p.date}</span>
          </div>
          <div className="flex gap-6">
            <span className="label w-20 shrink-0">Time</span>
            <span className="font-body font-light text-base md:text-lg">{p.time}</span>
          </div>
          <div className="flex gap-6">
            <span className="label w-20 shrink-0">Venue</span>
            <span className="font-body font-light text-base md:text-lg">
              {p.venue}
              {p.venueNote && (
                <>
                  <br />
                  <span className="text-black/50 text-sm">{p.venueNote}</span>
                </>
              )}
            </span>
          </div>
          <div className="flex gap-6">
            <span className="label w-20 shrink-0">Access</span>
            <span className="font-body font-light text-base md:text-lg">{p.access}</span>
          </div>
        </div>

        <Link
          to={p.path}
          data-testid={`${testIdPrefix}-more-info-btn`}
          className="group inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-black/80 transition-all"
        >
          More Info
          <ArrowUpRight
            size={16}
            strokeWidth={1}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </Link>
      </motion.div>
    </div>
  );
}
